import { useCallback, useEffect, useMemo } from 'react';
import { Listbox, ListboxItem, Selection } from '@nextui-org/react';
import { FaAngleRight } from 'react-icons/fa6';
import Icon from '../base/Icon';
import ChatListItem from './ChatListItem';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  selectActors,
  selectCurrentClient,
  selectCurrentClientId,
  setCurrentClient,
} from '../../store/client/clientSlice';
import {
  clearMessagesWithClient,
  selectChats,
} from '../../store/chat/chatSlice';
import { GENERIC_APP } from '../../config/generic';
import { SOS_APP } from '../../config/sos';
import { Actor } from '../../types';
import { BASE_APP } from '../../config/base';
import { selectUserAuth } from '../../store/auth/authSlice';

export default function ChatListActors() {
  const dispatch = useAppDispatch();
  const actors = useAppSelector(selectActors);
  const chats = useAppSelector(selectChats);
  const currentClientId = useAppSelector(selectCurrentClientId);
  const currentClient = useAppSelector(selectCurrentClient);
  const userAuth = useAppSelector(selectUserAuth);

  const actorsArray = useMemo(
    () => Array.from(Object.values(actors)),
    [actors]
  );

  const filterActors = useCallback(
    (actorsArray: Actor[]) => {
      let allowedActors: string[] = [];

      if (userAuth?.actor?.length) {
        allowedActors = allowedActors.concat(userAuth.actor);
      } else {
        if (userAuth?.role !== 'user_trainee') {
          if (process.env.REACT_APP_DEPLOYMENT_TYPE === 'SOS') {
            allowedActors = allowedActors.concat(SOS_APP.actors);
          } else {
            allowedActors = allowedActors.concat(GENERIC_APP.actors);
          }
          allowedActors = allowedActors.concat(BASE_APP.actors);
        }
      }

      return actorsArray.filter((actor) =>
        allowedActors.includes(actor.userId)
      );
    },
    [userAuth]
  );

  const filteredActors = useMemo(
    () => filterActors(actorsArray),
    [actorsArray, filterActors]
  );

  const actorIds = useMemo(
    () => filteredActors.map((c) => c.userId),
    [filteredActors]
  );

  useEffect(() => {
    if (!currentClientId || !actorIds.includes(currentClientId)) {
      // current client is not an actor
      return;
    }
    if (currentClientId in chats) {
      // current client has a chat
      return;
    }

    dispatch(clearMessagesWithClient(currentClientId));
  }, [dispatch, actorIds, chats, currentClientId]);

  const handleSelectionChange = (keys: Selection) => {
    if (keys === 'all') {
      return;
    }
    for (const k of keys.values()) {
      const compositeId = k + '_' + userAuth?.username;
      const exists = compositeId in chats;
      dispatch(setCurrentClient(exists ? compositeId : k.toString()));
    }
  };

  return (
    <div className="relative flex flex-col mb-2">
      <Listbox
        items={filteredActors}
        itemClasses={{ base: 'rounded-medium' }}
        variant="flat"
        selectionMode="single"
        selectedKeys={currentClient?.userId ? [currentClient.userId] : []}
        onSelectionChange={handleSelectionChange}
        emptyContent={<p>No chats.</p>}
        aria-label="Actors chat list"
      >
        {(c) => (
          <ListboxItem
            key={c.userId}
            textValue={c.userId}
            selectedIcon={(props) =>
              props.isSelected && (
                <Icon icon={FaAngleRight} className="w-3 h-3" />
              )
            }
            className={`${currentClient ? (c.userId === currentClient.userId ? 'highlight-list-item' : '') : ''}`}
          >
            <ChatListItem
              showAvatar
              client={c}
              messages={{
                actor_description: {
                  messageId: 'actor_description',
                  role: 'assistant',
                  content: c.userId
                    .split('_')
                    .map((s) => s.trim())
                    .filter((s) => !!s)
                    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
                    .join(' '),
                  createdAt: Date.now().toString(),
                },
              }}
            />
          </ListboxItem>
        )}
      </Listbox>
    </div>
  );
}
